import { create } from 'zustand'
import { makeId } from '../lib/id'
import type {
  Floor,
  FurnitureInstance,
  Opening,
  Project,
  PropertyType,
  Room,
  SelectionRef,
  Stair,
  ToolId,
  Wall,
} from '../types/project'
import { getCatalogItem } from '../data/furnitureCatalog'
import type { Point2D } from '../types/project'

export type ViewMode = '2d' | '3d'

const MAX_HISTORY = 60

function cloneProject(project: Project): Project {
  return typeof structuredClone === 'function'
    ? structuredClone(project)
    : (JSON.parse(JSON.stringify(project)) as Project)
}

function emptyFloor(level: number, name: string): Floor {
  return {
    id: makeId('floor'),
    name,
    level,
    height: 2.6,
    walls: [],
    openings: [],
    rooms: [],
    furniture: [],
    stairs: [],
  }
}

export function createNewProject(params: {
  name: string
  propertyType: PropertyType
  width: number
  length: number
}): Project {
  const now = new Date().toISOString()
  const floor = emptyFloor(0, 'Planta baja')
  return {
    id: makeId('proj'),
    name: params.name,
    propertyType: params.propertyType,
    createdAt: now,
    updatedAt: now,
    width: params.width,
    length: params.length,
    floors: [floor],
    activeFloorId: floor.id,
    settings: { unit: 'm', gridSize: 0.25, snapEnabled: true },
  }
}

interface EditorState {
  project: Project | null
  selection: SelectionRef | null
  activeTool: ToolId
  viewMode: ViewMode
  past: Project[]
  future: Project[]

  // proyecto
  loadProject: (project: Project) => void
  closeProject: () => void
  renameProject: (name: string) => void
  updateSettings: (patch: Partial<Project['settings']>) => void

  // navegación
  setActiveFloor: (floorId: string) => void
  addFloor: () => void
  removeFloor: (floorId: string) => void
  setTool: (tool: ToolId) => void
  setViewMode: (mode: ViewMode) => void
  select: (ref: SelectionRef | null) => void

  // muros
  addWall: (wall: Omit<Wall, 'id'>) => string
  updateWall: (id: string, patch: Partial<Wall>) => void
  removeWall: (id: string) => void

  // habitaciones
  addRoom: (room: Omit<Room, 'id'>) => string
  updateRoom: (id: string, patch: Partial<Room>) => void
  removeRoom: (id: string) => void
  /** Crea una habitación rectangular junto con sus 4 muros perimetrales. */
  createRectRoom: (input: {
    origin: Point2D
    width: number
    length: number
    type: Room['type']
    name: string
    color: string
    wallThickness: number
    wallHeight: number
  }) => string
  /** Redimensiona una habitación rectangular y sus 4 muros asociados, manteniendo fija la esquina superior izquierda. */
  resizeRectRoom: (id: string, width: number, length: number) => void
  moveRoomBy: (id: string, dx: number, dy: number) => void

  // huecos (puertas/ventanas)
  addOpening: (opening: Omit<Opening, 'id'>) => string
  updateOpening: (id: string, patch: Partial<Opening>) => void
  removeOpening: (id: string) => void

  // mobiliario
  addFurnitureFromCatalog: (catalogId: string, position: { x: number; y: number }) => string
  updateFurniture: (id: string, patch: Partial<FurnitureInstance>) => void
  removeFurniture: (id: string) => void
  duplicateFurniture: (id: string) => string | null

  // escaleras
  addStair: (stair: Omit<Stair, 'id'>) => string
  updateStair: (id: string, patch: Partial<Stair>) => void
  removeStair: (id: string) => void

  undo: () => void
  redo: () => void
  canUndo: () => boolean
  canRedo: () => boolean
}

function activeFloor(project: Project): Floor {
  const floor = project.floors.find((f) => f.id === project.activeFloorId)
  if (!floor) throw new Error('No hay piso activo válido en el proyecto.')
  return floor
}

export const useEditorStore = create<EditorState>((set, get) => {
  /** Aplica una mutación al proyecto actual registrando el estado previo en el historial. */
  function commit(mutator: (draft: Project) => void) {
    const current = get().project
    if (!current) return
    const snapshot = cloneProject(current)
    const next = cloneProject(current)
    mutator(next)
    next.updatedAt = new Date().toISOString()
    set((state) => ({
      project: next,
      past: [...state.past, snapshot].slice(-MAX_HISTORY),
      future: [],
    }))
  }

  return {
    project: null,
    selection: null,
    activeTool: 'select',
    viewMode: '2d',
    past: [],
    future: [],

    loadProject: (project) => set({ project, past: [], future: [], selection: null, activeTool: 'select' }),
    closeProject: () => set({ project: null, past: [], future: [], selection: null }),
    renameProject: (name) => commit((p) => { p.name = name }),
    updateSettings: (patch) => commit((p) => { p.settings = { ...p.settings, ...patch } }),

    setActiveFloor: (floorId) =>
      set((state) => (state.project ? { project: { ...state.project, activeFloorId: floorId }, selection: null } : state)),
    addFloor: () =>
      commit((p) => {
        const level = p.floors.length
        const names = ['Planta baja', 'Segundo piso', 'Tercer piso', 'Cuarto piso']
        const floor = emptyFloor(level, names[level] ?? `Piso ${level + 1}`)
        p.floors.push(floor)
        p.activeFloorId = floor.id
      }),
    removeFloor: (floorId) =>
      commit((p) => {
        if (p.floors.length <= 1) return
        p.floors = p.floors.filter((f) => f.id !== floorId)
        if (p.activeFloorId === floorId) p.activeFloorId = p.floors[0].id
      }),
    setTool: (tool) => set({ activeTool: tool, selection: null }),
    setViewMode: (mode) => set({ viewMode: mode }),
    select: (ref) => set({ selection: ref }),

    addWall: (wall) => {
      const id = makeId('wall')
      commit((p) => {
        activeFloor(p).walls.push({ ...wall, id })
      })
      return id
    },
    updateWall: (id, patch) =>
      commit((p) => {
        const floor = activeFloor(p)
        const wall = floor.walls.find((w) => w.id === id)
        if (wall) Object.assign(wall, patch)
      }),
    removeWall: (id) =>
      commit((p) => {
        const floor = activeFloor(p)
        floor.walls = floor.walls.filter((w) => w.id !== id)
        floor.openings = floor.openings.filter((o) => o.wallId !== id)
      }),

    addRoom: (room) => {
      const id = makeId('room')
      commit((p) => {
        activeFloor(p).rooms.push({ ...room, id })
      })
      return id
    },
    updateRoom: (id, patch) =>
      commit((p) => {
        const floor = activeFloor(p)
        const room = floor.rooms.find((r) => r.id === id)
        if (room) Object.assign(room, patch)
      }),
    removeRoom: (id) =>
      commit((p) => {
        const floor = activeFloor(p)
        const room = floor.rooms.find((r) => r.id === id)
        floor.rooms = floor.rooms.filter((r) => r.id !== id)
        if (room) {
          floor.walls = floor.walls.filter((w) => !room.wallIds.includes(w.id))
          floor.openings = floor.openings.filter((o) => !room.wallIds.includes(o.wallId))
        }
      }),
    createRectRoom: (input) => {
      const roomId = makeId('room')
      const { origin, width, length, wallThickness, wallHeight } = input
      const corners: Point2D[] = [
        { x: origin.x, y: origin.y },
        { x: origin.x + width, y: origin.y },
        { x: origin.x + width, y: origin.y + length },
        { x: origin.x, y: origin.y + length },
      ]
      const wallIds = [makeId('wall'), makeId('wall'), makeId('wall'), makeId('wall')]
      commit((p) => {
        const floor = activeFloor(p)
        for (let i = 0; i < 4; i++) {
          floor.walls.push({
            id: wallIds[i],
            start: corners[i],
            end: corners[(i + 1) % 4],
            thickness: wallThickness,
            height: wallHeight,
            material: 'Pintura',
            color: '#b6ae9c',
          })
        }
        floor.rooms.push({
          id: roomId,
          name: input.name,
          type: input.type,
          color: input.color,
          polygon: corners,
          wallIds,
          height: wallHeight,
          floorId: floor.id,
        })
      })
      return roomId
    },
    resizeRectRoom: (id, width, length) =>
      commit((p) => {
        const floor = activeFloor(p)
        const room = floor.rooms.find((r) => r.id === id)
        if (!room || room.wallIds.length !== 4) return
        const origin = room.polygon[0]
        const corners: Point2D[] = [
          { x: origin.x, y: origin.y },
          { x: origin.x + width, y: origin.y },
          { x: origin.x + width, y: origin.y + length },
          { x: origin.x, y: origin.y + length },
        ]
        room.polygon = corners
        room.wallIds.forEach((wallId, i) => {
          const wall = floor.walls.find((w) => w.id === wallId)
          if (wall) {
            wall.start = corners[i]
            wall.end = corners[(i + 1) % 4]
          }
        })
      }),
    moveRoomBy: (id, dx, dy) =>
      commit((p) => {
        const floor = activeFloor(p)
        const room = floor.rooms.find((r) => r.id === id)
        if (!room) return
        room.polygon = room.polygon.map((pt) => ({ x: pt.x + dx, y: pt.y + dy }))
        room.wallIds.forEach((wallId) => {
          const wall = floor.walls.find((w) => w.id === wallId)
          if (wall) {
            wall.start = { x: wall.start.x + dx, y: wall.start.y + dy }
            wall.end = { x: wall.end.x + dx, y: wall.end.y + dy }
          }
        })
      }),

    addOpening: (opening) => {
      const id = makeId('opening')
      commit((p) => {
        activeFloor(p).openings.push({ ...opening, id })
      })
      return id
    },
    updateOpening: (id, patch) =>
      commit((p) => {
        const floor = activeFloor(p)
        const opening = floor.openings.find((o) => o.id === id)
        if (opening) Object.assign(opening, patch)
      }),
    removeOpening: (id) =>
      commit((p) => {
        const floor = activeFloor(p)
        floor.openings = floor.openings.filter((o) => o.id !== id)
      }),

    addFurnitureFromCatalog: (catalogId, position) => {
      const item = getCatalogItem(catalogId)
      if (!item) return ''
      const id = makeId('furn')
      commit((p) => {
        const instance: FurnitureInstance = {
          id,
          catalogId: item.id,
          name: item.name,
          category: item.category,
          position,
          rotationY: 0,
          width: item.defaultWidth,
          depth: item.defaultDepth,
          height: item.defaultHeight,
          color: item.defaultColor,
        }
        activeFloor(p).furniture.push(instance)
      })
      return id
    },
    updateFurniture: (id, patch) =>
      commit((p) => {
        const floor = activeFloor(p)
        const item = floor.furniture.find((f) => f.id === id)
        if (item) Object.assign(item, patch)
      }),
    removeFurniture: (id) =>
      commit((p) => {
        const floor = activeFloor(p)
        floor.furniture = floor.furniture.filter((f) => f.id !== id)
      }),
    duplicateFurniture: (id) => {
      const current = get().project
      if (!current) return null
      const floor = activeFloor(current)
      const source = floor.furniture.find((f) => f.id === id)
      if (!source) return null
      const newId = makeId('furn')
      commit((p) => {
        const target = activeFloor(p)
        target.furniture.push({
          ...source,
          id: newId,
          position: { x: source.position.x + 0.3, y: source.position.y + 0.3 },
        })
      })
      return newId
    },

    addStair: (stair) => {
      const id = makeId('stair')
      commit((p) => {
        activeFloor(p).stairs.push({ ...stair, id })
      })
      return id
    },
    updateStair: (id, patch) =>
      commit((p) => {
        const floor = activeFloor(p)
        const stair = floor.stairs.find((s) => s.id === id)
        if (stair) Object.assign(stair, patch)
      }),
    removeStair: (id) =>
      commit((p) => {
        const floor = activeFloor(p)
        floor.stairs = floor.stairs.filter((s) => s.id !== id)
      }),

    undo: () =>
      set((state) => {
        if (state.past.length === 0 || !state.project) return state
        const previous = state.past[state.past.length - 1]
        return {
          project: previous,
          past: state.past.slice(0, -1),
          future: [cloneProject(state.project), ...state.future].slice(0, MAX_HISTORY),
          selection: null,
        }
      }),
    redo: () =>
      set((state) => {
        if (state.future.length === 0 || !state.project) return state
        const next = state.future[0]
        return {
          project: next,
          past: [...state.past, cloneProject(state.project)].slice(-MAX_HISTORY),
          future: state.future.slice(1),
          selection: null,
        }
      }),
    canUndo: () => get().past.length > 0,
    canRedo: () => get().future.length > 0,
  }
})

export function useActiveFloor(): Floor | null {
  const project = useEditorStore((s) => s.project)
  if (!project) return null
  return project.floors.find((f) => f.id === project.activeFloorId) ?? null
}
